import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  alpha,
  Box,
  Typography,
  Container,
  Alert,
  Avatar,
  Chip,
  Divider,
  useTheme,
  Fade,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Autocomplete,
  Stack,
} from "@mui/material";
import {
  PersonAdd as RegisterIcon,
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  AccountBalanceWallet as CurrencyIcon,
  InsightsOutlined,
  TimelineOutlined,
  ShieldOutlined,
} from "@mui/icons-material";
import BrandIcon from "../../components/common/BrandIcon";
import { useRegister } from "../../hooks/auth";
import { ApiError } from "../../error/errorTypes";
import { LOGIN_ROUTE_PATH, VERIFY_EMAIL_ROUTE_PATH, VERIFY_EMAIL_CONFIRM_ROUTE_PATH } from "../../utils/constants/AppConstants";
import { currencyOptions, popularCurrencyCodes } from "../../utils/currencies";
import PasswordStrengthMeter from "../../components/common/PasswordStrengthMeter";
import { isPasswordCompliant } from "../../utils/passwordPolicy";
import { validateInternationalName } from "../../utils/nameValidation";
import SurfaceCard from "../../components/common/ui/SurfaceCard";
import FormField from "../../components/common/ui/FormField";
import PrimaryButton from "../../components/common/ui/PrimaryButton";

const REGISTER_SUCCESS_REDIRECT_DELAY_MS = 1500;

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [currency, setCurrency] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { mutate: register, isPending: isLoading } = useRegister();

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    password: "",
    repeatPassword: "",
    currency: "",
  });

  const [touched, setTouched] = useState({
    name: false,
    email: false,
    password: false,
    repeatPassword: false,
    currency: false,
  });

  type Field = keyof typeof errors;

  const values: Record<Field, string> = {
    name,
    email,
    password,
    repeatPassword,
    currency,
  };

  const validateEmail = (value: string): string =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
      ? ""
      : "Please enter a valid email address like name@example.com.";

  const validatePassword = (value: string): string =>
    isPasswordCompliant(value) ? "" : "Use at least 8 chars with uppercase, lowercase, number, and symbol.";

  const validateName = (value: string): string => validateInternationalName(value);

  const validateRepeatPassword = (value: string, original: string): string =>
    value === original ? "" : "Passwords don't match. Please try again.";

  const validateCurrency = (value: string): string =>
    value ? "" : "Please select your preferred currency.";

  const setters: Record<Field, (v: string) => void> = {
    name: setName,
    email: setEmail,
    password: (v) => {
      setPassword(v);
      if (touched.repeatPassword) {
        setErrors((prev) => ({
          ...prev,
          repeatPassword: validateRepeatPassword(repeatPassword, v),
        }));
      }
    },
    repeatPassword: setRepeatPassword,
    currency: setCurrency,
  };

  const validators = (pwd: string): Record<Field, (v: string) => string> => ({
    name: validateName,
    email: validateEmail,
    password: validatePassword,
    repeatPassword: (v: string) => validateRepeatPassword(v, pwd),
    currency: validateCurrency,
  });

  const handleBlur = (field: Field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const v = values[field];
    setErrors((prev) => ({
      ...prev,
      [field]: validators(password)[field](v),
    }));
  };

  const handleChange = (field: Field) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setters[field](v);

    if (touched[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: validators(field === "password" ? v : password)[field](v),
      }));
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    setTouched({
      name: true,
      email: true,
      password: true,
      repeatPassword: true,
      currency: true,
    });

    const newErrors = {
      name: validateName(name),
      email: validateEmail(email),
      password: validatePassword(password),
      repeatPassword: validateRepeatPassword(repeatPassword, password),
      currency: validateCurrency(currency),
    };

    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some((e) => e !== "");
    if (hasErrors) {
      return;
    }

    const publicBase = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
    const path = `${publicBase}${VERIFY_EMAIL_CONFIRM_ROUTE_PATH}`;
    const verificationCallbackUrl = new URL(path, window.location.origin).toString();

    register(
      { email, password, name, currency, verificationCallbackUrl },
      {
        onSuccess: () => {
          setSuccessMessage("Account created successfully! We've sent a verification email.");
          // Short delay to show success message before redirect to verify page
          setTimeout(() => {
            navigate(VERIFY_EMAIL_ROUTE_PATH, { state: { email } });
          }, REGISTER_SUCCESS_REDIRECT_DELAY_MS);
        },
        onError: (error) => {
          console.error("Registration failed:", error);
          if (error instanceof ApiError) {
            setSubmitError(error.message);
          } else {
            setSubmitError("Registration failed. Please try again.");
          }
        }
      }
    );
  };

  const handleClickShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const handleClickShowRepeatPassword = () => {
    setShowRepeatPassword((show) => !show);
  };

  // Group currencies: popular first, then all others alphabetically
  const popularCurrencies = currencyOptions.filter(c => popularCurrencyCodes.includes(c.code));
  const otherCurrencies = currencyOptions
    .filter(c => !popularCurrencyCodes.includes(c.code))
    .sort((a, b) => a.name.localeCompare(b.name));
  
  const groupedCurrencies = [
    ...popularCurrencies,
    ...otherCurrencies,
  ];

  type CurrencyOption = (typeof currencyOptions)[number];

  const currencyAliases: Record<string, string[]> = {
    RON: ["lei", "leu", "romanian leu", "romanian", "romania"],
    GBP: ["sterling", "pound sterling"],
    EUR: ["eurozone", "eu"],
    USD: ["dollar", "us"],
  };

  const normalizeSearch = (value: string) =>
    value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const getCurrencySearchText = (option: CurrencyOption) => {
    const aliases = currencyAliases[option.code] || [];
    return normalizeSearch([
      option.name,
      option.code,
      option.symbol,
      ...aliases,
    ].join(" "));
  };

  const filterCurrencyOptions = (options: CurrencyOption[], inputValue: string) => {
    const query = normalizeSearch(inputValue);
    if (!query) {
      return options;
    }

    const startsWithMatches: CurrencyOption[] = [];
    const containsMatches: CurrencyOption[] = [];

    options.forEach((option) => {
      const searchable = getCurrencySearchText(option);
      if (searchable.startsWith(query)) {
        startsWithMatches.push(option);
      } else if (searchable.includes(query)) {
        containsMatches.push(option);
      }
    });

    return [...startsWithMatches, ...containsMatches];
  };

  const inputSx = {
    '& .MuiInputLabel-root': { color: theme.palette.text.secondary },
    '& .MuiOutlinedInput-root': {
      background: alpha(theme.palette.background.paper, 0.62),
      borderRadius: theme.radii?.medium ?? 10,
      color: theme.palette.text.primary,
      transition: 'all 0.22s ease',
      '& input': { color: theme.palette.text.primary },
      '& input:-webkit-autofill': {
        WebkitTextFillColor: theme.palette.text.primary,
        caretColor: theme.palette.text.primary,
        WebkitBoxShadow: `0 0 0 100px ${alpha(theme.palette.background.paper, 0.62)} inset`,
        borderRadius: theme.radii?.medium ?? 10,
        transition: 'background-color 9999s ease-out, color 9999s ease-out',
      },
      '& fieldset': { borderColor: alpha(theme.palette.text.primary, 0.1) },
      '&:hover fieldset': { borderColor: alpha(theme.palette.text.primary, 0.22) },
      '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main, borderWidth: 2 },
      '&.Mui-focused': { boxShadow: `0 10px 24px ${alpha(theme.palette.primary.main, 0.2)}` },
    },
  };

  const leftPaneHighlights = [
    { icon: <InsightsOutlined fontSize="small" />, text: "Clear monthly insights from day one" },
    { icon: <TimelineOutlined fontSize="small" />, text: "Track income and expenses in one flow" },
    { icon: <ShieldOutlined fontSize="small" />, text: "Secure account with verified email access" },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `radial-gradient(circle at 20% 16%, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 38%), ${theme.palette.background.default}`,
        display: "flex",
        alignItems: "center",
        justifyContent: 'center',
        py: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="lg">
        <Fade in={true} timeout={800}>
          <SurfaceCard
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
              background: alpha(theme.palette.background.paper, 0.9),
              backdropFilter: "blur(8px)",
              boxShadow: "0 14px 44px rgba(2, 6, 23, 0.55)",
            }}
          >
            <Grid container>
              <Grid
                item
                xs={12}
                md={5}
                sx={{
                  p: { xs: 2.6, sm: 3.2, md: 3.4 },
                  borderRight: { md: `1px solid ${alpha(theme.palette.text.primary, 0.08)}` },
                  borderBottom: { xs: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`, md: "none" },
                  background: `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.background.paper, 0.35)})`,
                }}
              >
                <Box sx={{ mb: 2.2 }}>
                  <BrandIcon size={48} />
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: "-0.02em",
                    fontFamily: '"Space Grotesk", "Manrope", sans-serif',
                  }}
                >
                  Create Account
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8, maxWidth: 360 }}>
                  Join PocketBalance and start managing your finances with a clean, focused dashboard.
                </Typography>

                <Stack spacing={1} sx={{ mt: 2.2 }}>
                  {leftPaneHighlights.map((item) => (
                    <Stack key={item.text} direction="row" spacing={1} alignItems="center">
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          bgcolor: alpha(theme.palette.primary.main, 0.2),
                          color: theme.palette.primary.light,
                        }}
                      >
                        {item.icon}
                      </Avatar>
                      <Typography variant="body2" color="text.secondary">
                        {item.text}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Chip
                  label="No credit card required"
                  sx={{
                    mt: 2.1,
                    borderRadius: "10px",
                    color: theme.palette.primary.light,
                    backgroundColor: alpha(theme.palette.primary.main, 0.16),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={7} sx={{ p: { xs: 2.6, sm: 3.2, md: 3.8 } }}>
                <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 2.2 }}>
                  <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(theme.palette.primary.main, 0.2), color: theme.palette.primary.light }}>
                    <RegisterIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                      Set up your account
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Your data starts syncing as soon as you verify email.
                    </Typography>
                  </Box>
                </Stack>

                {/* Success Message */}
                {successMessage && (
                  <Alert
                    severity="success"
                    onClose={() => setSuccessMessage(null)}
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      background: alpha(theme.palette.success.main, 0.08),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.16)}`,
                    }}
                  >
                    {successMessage}
                  </Alert>
                )}

                {/* Error Alert */}
                {submitError && (
                  <Alert
                    severity="error"
                    onClose={() => setSubmitError(null)}
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      background: alpha(theme.palette.error.main, 0.08),
                      border: `1px solid ${alpha(theme.palette.error.main, 0.16)}`,
                    }}
                  >
                    {submitError}
                  </Alert>
                )}

                {/* Form */}
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1.8 }}>
                  <Grid container spacing={2.4}>
                  {/* Name Field */}
                  <Grid item xs={12} sm={6}>
                    <FormField
                      fullWidth
                      id="name"
                      label="Full Name"
                      name="name"
                      autoComplete="name"
                      autoFocus
                      value={name}
                      onChange={handleChange("name")}
                      onBlur={() => handleBlur("name")}
                      error={!!errors.name && touched.name}
                      helperText={touched.name ? errors.name : `${name.length}/100 characters`}
                      required
                      disabled={isLoading}
                      sx={inputSx}
                      inputProps={{ maxLength: 100 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Email Field */}
                  <Grid item xs={12} sm={6}>
                    <FormField
                      fullWidth
                      id="email"
                      label="Email Address"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={handleChange("email")}
                      onBlur={() => handleBlur("email")}
                      error={!!errors.email && touched.email}
                      helperText={touched.email ? errors.email : "We'll never share your email"}
                      required
                      disabled={isLoading}
                      sx={inputSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Password Field */}
                  <Grid item xs={12} sm={6}>
                    <FormField
                      fullWidth
                      name="password"
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={handleChange("password")}
                      onBlur={() => handleBlur("password")}
                      error={!!errors.password && touched.password}
                      helperText={touched.password ? errors.password : "Must include upper letters, lower letters, number, and symbol"}
                      required
                      disabled={isLoading}
                      sx={inputSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={(e) => e.preventDefault()}
                              edge="end"
                              disabled={isLoading}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <PasswordStrengthMeter password={password} />
                  </Grid>

                  {/* Repeat Password Field */}
                  <Grid item xs={12} sm={6}>
                    <FormField
                      fullWidth
                      name="repeatPassword"
                      label="Confirm Password"
                      type={showRepeatPassword ? 'text' : 'password'}
                      id="repeatPassword"
                      autoComplete="new-password"
                      value={repeatPassword}
                      onChange={handleChange("repeatPassword")}
                      onBlur={() => handleBlur("repeatPassword")}
                      error={!!errors.repeatPassword && touched.repeatPassword}
                      helperText={touched.repeatPassword ? errors.repeatPassword : "Must match your password"}
                      required
                      disabled={isLoading}
                      sx={inputSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowRepeatPassword}
                              onMouseDown={(e) => e.preventDefault()}
                              edge="end"
                              disabled={isLoading}
                            >
                              {showRepeatPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                                    {/* Currency Field */}
                  <Grid item xs={12}>
                    <Autocomplete
                      fullWidth
                      value={currencyOptions.find(c => c.code === currency) || null}
                      onChange={(_, newValue) => {
                        const newCurrency = newValue?.code || "";
                        setCurrency(newCurrency);
                        if (touched.currency) {
                          setErrors((prev) => ({
                            ...prev,
                            currency: validateCurrency(newCurrency),
                          }));
                        }
                      }}
                      onBlur={() => handleBlur("currency")}
                      options={groupedCurrencies}
                      filterOptions={(options, state) => filterCurrencyOptions(options, state.inputValue)}
                      groupBy={(option) => 
                        popularCurrencyCodes.includes(option.code) ? "Popular Currencies" : "All Currencies"
                      }
                      getOptionLabel={(option) => `${option.name} (${option.code})`}
                      noOptionsText="No currency found. Try name, code, or symbol (e.g., RON, lei, $, €)."
                      renderOption={(props, option) => (
                        <Box component="li" {...props} sx={{ display: 'flex', gap: 1 }}>
                          <Typography sx={{ fontWeight: 600, minWidth: 30 }}>
                            {option.symbol}
                          </Typography>
                          <Typography>
                            {option.name}
                          </Typography>
                          <Typography color="text.secondary">
                            ({option.code})
                          </Typography>
                        </Box>
                      )}
                      disabled={isLoading}
                      renderInput={(params) => (
                        <FormField
                          {...params}
                          label="Preferred Currency"
                          error={!!errors.currency && touched.currency}
                          helperText={touched.currency ? errors.currency : "Search by name, code, or symbol (e.g., Romanian, RON, lei)."}
                          required
                          sx={inputSx}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <CurrencyIcon color="action" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <PrimaryButton
                  type="submit"
                  fullWidth
                  size="large"
                  disabled={isLoading}
                  startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <RegisterIcon />}
                  sx={{
                    py: 1.5,
                    mt: 3.2,
                    mb: 2.6,
                    '&:disabled': {
                      background: theme.palette.action.disabledBackground,
                    },
                  }}
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </PrimaryButton>

                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Already have an account?
                  </Typography>
                </Divider>

                <Box sx={{ textAlign: "center" }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => navigate(LOGIN_ROUTE_PATH)}
                    sx={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      fontSize: '1rem',
                      fontFamily: theme.typography.fontFamily,
                      '&:hover': {
                        color: theme.palette.primary.dark,
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Sign in to your account
                  </Box>
                </Box>
                </Box>
              </Grid>
            </Grid>
          </SurfaceCard>
        </Fade>
      </Container>
    </Box>
  );
};

export default RegisterPage;
